/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon 
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';

const WaterManagement = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  const [waters, setWaters] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWater, setEditingWater] = useState(null);
  const [newWater, setNewWater] = useState({
    description: '',
    label: '',
    facility: ''
  });

  // Pagination and search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredWaters, setFilteredWaters] = useState([]);

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredWaters,
    { defaultSortColumn: 'water', defaultSortOrder: 'asc' }
  );

  const fetchData = useCallback(async () => {
    try {
      const [watersRes, facilitiesRes] = await Promise.all([
        axios.get('/api/waters'),
        axios.get('/api/facilities')
      ]);
      
      let fetchedWaters = watersRes.data;
      if (selectedFacility !== 'all') {
        fetchedWaters = fetchedWaters.filter(
          water => water.facility?._id === selectedFacility
        );
      }
      
      setWaters(fetchedWaters);
      setFilteredWaters(fetchedWaters);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const filtered = waters.filter(water => 
      Object.values(water).some(value => 
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      ) ||
      water.facility?.facility_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredWaters(filtered);
    setPage(0);
  }, [searchQuery, waters]);

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedWaters = sortedData.slice(startIndex, endIndex);

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreate = async () => {
    try {
      if (!newWater.description || !newWater.label || !newWater.facility) {
        alert('Please fill in all required fields');
        return;
      }

      await axios.post('/api/waters', newWater);
      await fetchData();
      toast.success('Water type created successfully');
      setNewWater({
        description: '',
        label: '',
        facility: ''
      });
    } catch (error) {
      console.error('Error creating water:', error);
      toast.error(error.response?.data?.error || 'Failed to create water');
    }
  };

  const handleUpdate = async () => {
    try {
      if (!editingWater.description || !editingWater.label || !editingWater.facility) {
        alert('Please fill in all required fields');
        return;
      }

      const waterData = {
        description: editingWater.description.trim(),
        label: editingWater.label.trim(),
        facility: typeof editingWater.facility === 'object' ? editingWater.facility._id : editingWater.facility
      };

      await axios.put(`/api/waters/${editingWater._id}`, waterData);
      await fetchData();
      setEditDialogOpen(false);
      setEditingWater(null);
    } catch (error) {
      console.error('Error updating water:', error);
      alert(error.response?.data?.error || 'Failed to update water');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this water type?')) {
      try {
        await axios.delete(`/api/waters/${id}`);
        await fetchData();
      } catch (error) {
        console.error('Error deleting water:', error);
        alert('Failed to delete water');
      }
    }
  };

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{ mx: 1 }}
          size="small"
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
        <span>entries</span>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page"
          onClick={() => handleChangePage(0)}
          disabled={page === 0}
        >
          <FirstPage />
        </IconButton>
        <IconButton aria-label="Previous page"
          onClick={() => handleChangePage(page - 1)}
          disabled={page === 0}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Box sx={{ mx: 2 }}>
          Page {page + 1} of {Math.ceil(filteredWaters.length / rowsPerPage)}
        </Box>
        <IconButton aria-label="Next page"
          onClick={() => handleChangePage(page + 1)}
          disabled={page >= Math.ceil(filteredWaters.length / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton aria-label="Last page"
          onClick={() => handleChangePage(Math.ceil(filteredWaters.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(filteredWaters.length / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="water-management">
      <h2>Water Management</h2>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search water types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={() => setCreateDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Add New Water Type
      </Button>

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader column="label" label="Label" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="facility.facility_name" label="Facility" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedWaters.map(water => (
            <tr key={water._id}>
              <td>{water.label}</td>
              <td>{water.description}</td>
              <td>{water.facility?.facility_name}</td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => {
                    setEditingWater(water);
                    setEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(water._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>Add New Water Type</DialogTitle>
        <DialogContent>
          <TextField
            label="Label"
            fullWidth
            margin="normal"
            value={newWater.label}
            onChange={(e) => setNewWater({...newWater, label: e.target.value})}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={newWater.description}
            onChange={(e) => setNewWater({...newWater, description: e.target.value})}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Facility</InputLabel>
            <Select
              value={newWater.facility}
              onChange={(e) => setNewWater({...newWater, facility: e.target.value})}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Water Type</DialogTitle>
        <DialogContent>
          <TextField
            label="Label"
            fullWidth
            margin="normal"
            value={editingWater?.label || ''}
            onChange={(e) => setEditingWater({...editingWater, label: e.target.value})}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={editingWater?.description || ''}
            onChange={(e) => setEditingWater({...editingWater, description: e.target.value})}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Facility</InputLabel>
            <Select
              value={editingWater?.facility?._id || ''}
              onChange={(e) => setEditingWater({...editingWater, facility: e.target.value})}
              label="Facility"
            >
              {facilities.map(facility => (
                <MenuItem key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WaterManagement;